/**
 * Connector test panel component.
 * Provides UI for testing connector connections.
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { connectorService } from '../../services/connectorService';
import { useNotificationStore } from '../../store/notificationStore';
import type { TestConnectionResponse } from '../../types/connector';

export interface ConnectorTestPanelProps {
  instanceId: string;
  connectorName: string;
  onTestComplete?: (result: TestConnectionResponse) => void;
}

/**
 * Connector test panel component.
 */
export const ConnectorTestPanel = ({
  instanceId,
  connectorName,
  onTestComplete,
}: ConnectorTestPanelProps) => {
  const { addNotification } = useNotificationStore();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await connectorService.testConnection(instanceId);
      setTestResult(result);
      onTestComplete?.(result);

      if (result.success) {
        addNotification(`Connection test successful for ${connectorName}`, 'success');
      } else {
        addNotification(`Connection test failed: ${result.message}`, 'error');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Connection test failed';
      setTestResult({
        success: false,
        message,
      });
      addNotification(message, 'error');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Test Connection
          </Typography>
          <Button
            variant="contained"
            startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleTest}
            disabled={testing}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
        </Box>

        {testResult && (
          <>
            <Divider sx={{ my: 2 }} />
            {testResult.success ? (
              <Alert
                icon={<CheckCircleIcon />}
                severity="success"
                sx={{ mb: 2 }}
              >
                Connection test successful!
                {testResult.durationMs && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Response time: {testResult.durationMs}ms
                  </Typography>
                )}
              </Alert>
            ) : (
              <Alert
                icon={<ErrorIcon />}
                severity="error"
                sx={{ mb: 2 }}
              >
                Connection test failed: {testResult.message}
                {testResult.statusCode && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    Status Code: {testResult.statusCode}
                  </Typography>
                )}
              </Alert>
            )}
          </>
        )}

        <Typography variant="body2" color="text.secondary">
          Test the connection to verify that authentication credentials are valid and the connector can communicate with the external service.
        </Typography>
      </CardContent>
    </Card>
  );
};
